import { Subscription } from 'rxjs';
import { FormGroup, FormArray, FormControl, Validators } from '@angular/forms';

import { OfferProduct, Offer, FeasibilityStatus, BocOpt, OfferFamilyVolume } from './offer';

function convertNullToUndefined<T>(value: T): T | undefined {
	if (value === null) {
		return undefined;
	}
	return value;
}

export class FormFamilyByVolume extends FormGroup {
	readonly familybyvolume: OfferFamilyVolume;
	constructor(familybyvolume: OfferFamilyVolume) {
		super({
			quantity: new FormControl(familybyvolume.quantity, [Validators.max(99_999_999.99)])
		});
		this.familybyvolume = familybyvolume;
	}

	getFormQuantity() {
		return <FormControl> this.get('quantity');
	}

	getFamilyByVolume() {
		this.familybyvolume.quantity = convertNullToUndefined(
			<number | undefined> this.getFormQuantity().value
		);
		return this.familybyvolume;
	}

	setFamilybyvolume(familybyvolume: OfferFamilyVolume) {
		if (familybyvolume.quantity !== undefined) {
			this.familybyvolume.quantity = this.familybyvolume.quantity === undefined
				? familybyvolume.quantity
				: this.familybyvolume.quantity + familybyvolume.quantity;
			this.getFormQuantity().setValue(this.familybyvolume.quantity);
		}
		if (this.familybyvolume.pcpType === undefined && familybyvolume.pcpType !== undefined) {
			this.familybyvolume.pcpType = familybyvolume.pcpType;
		}
		if (this.familybyvolume.pcpValue === undefined && familybyvolume.pcpValue !== undefined) {
			this.familybyvolume.pcpValue = familybyvolume.pcpValue;
		}
		if (this.familybyvolume.quantityUnit === undefined && familybyvolume.quantityUnit !== undefined) {
			this.familybyvolume.quantityUnit = familybyvolume.quantityUnit;
		}
	}
}

export class FormFamilyByVolumes extends FormArray {
	/**
	 * parent could decomposed into children, child keeps ref to its parent
	 */
	private readonly subscriptionforFamily: Subscription | undefined;
	private readonly parentFamilyByVolume: FormFamilyByVolumes | undefined;
	constructor(parentFamilyByVolume?: FormFamilyByVolumes) {
		super([], Validators.minLength(1));
		this.parentFamilyByVolume = parentFamilyByVolume;
		if (!this.parentFamilyByVolume) {
			return;
		}
		this.subscriptionforFamily = this.valueChanges.subscribe(() => {
			if (this.parentFamilyByVolume) {
				this.parentFamilyByVolume.updateValueAndValidity();
			}
		});
	}

	isValid() {
		if (this.length === 0) {
			return false;
		}
		for (let i = 0; i < this.length; i++) {
			if (this.getFormFamilyByVolumeAt(i).invalid) {
				return false;
			}
		}
		return true;
	}

	getFormFamilyByVolumeAt(index: number) {
		return <FormFamilyByVolume> this.at(index);
	}

	getFormFamilyByVolumes() {
		return this.controls.map(control => <FormFamilyByVolume> control);
	}

	addFamilyByVolume(familybyvolume: OfferFamilyVolume) {
		this.push(new FormFamilyByVolume(familybyvolume));
	}

	addFormFamilyByVolume(formfamilybyvolume: FormFamilyByVolume) {
		this.push(formfamilybyvolume);
	}

	getFamilyByVolumes() {
		const familybyvolumes: OfferFamilyVolume[] = [];
		for (let i = 0; i < this.length; i++) {
			familybyvolumes.push(this.getFormFamilyByVolumeAt(i).getFamilyByVolume());
		}
		return familybyvolumes.length === 0 ? undefined : familybyvolumes;
	}

	setFamilyByVolumes(familybyvolumes: OfferFamilyVolume[]) {
		for (const familyByVolume of familybyvolumes) {
			this.addFamilyByVolume(familyByVolume);
			if (this.parentFamilyByVolume) {
				this.parentFamilyByVolume.addFamilyByVolume(familyByVolume);
			}
		}
	}

	removeFamilyByVolume(familyCode: string | undefined) {
		for (let i = this.length - 1; i >= 0; i--) {
			if (this.getFormFamilyByVolumeAt(i).familybyvolume.familyCode !== familyCode) {
				continue;
			}
			this.removeAt(i);
			if (this.parentFamilyByVolume) {
				this.parentFamilyByVolume.removeFamilyByVolume(familyCode);
			}
		}
	}

	override clear() {
		if (this.parentFamilyByVolume) {
			for (let i = 0; i < this.length; i++) {
				this.parentFamilyByVolume.removeFamilyByVolume(
					this.getFormFamilyByVolumeAt(i).familybyvolume.familyCode
				);
			}
		}
		while (this.length > 0) {
			this.removeAt(0);
		}
	}

	onDestroy() {
		if (this.subscriptionforFamily) {
			this.subscriptionforFamily.unsubscribe();
		}
	}
}

export class FormProduct extends FormGroup {
	readonly product: OfferProduct;
	constructor(product: OfferProduct) {
		super({
			quantity: new FormControl(product.quantity),
			steelGrade: new FormControl(product.steelGrade, Validators.required),
			thicknessValue: new FormControl(product.thicknessValue, { validators: [
				Validators.required,
				Validators.min(0.1)
			] }),
			widthValue: new FormControl(product.widthValue, { validators: [
				Validators.required,
				Validators.min(1)
			] }),
			lengthValue: new FormControl(product.lengthValue, Validators.min(1)),
			shipToParty: new FormControl(product.shipToParty, Validators.required),
			endUser: new FormControl(product.endUser, Validators.required),
			category: new FormControl(product.category || ''),
			form: new FormControl(product.form || ''),
			subCategory: new FormControl(product.subCategory || ''),
			templateNumber: new FormControl(product.templateNumber || ''),
			a2citemNumber: new FormControl(product.a2citemNumber),
			inquiryNumber: new FormControl(product.inquiryNumber),
			productType: new FormControl(product.productType),
			configurationNumber: new FormControl(product.configurationNumber),
			isSimulationCompleted: new FormControl(product.isSimulationCompleted || '', Validators.required),
			configErrMsg: new FormControl(product.configErrMsg || ''),
			loader: new FormControl(product.loader || 'false'),
			uniqueRef: new FormControl(product.uniqueRef || ''),
			variableFormGroup: product.variableFormGroup || new FormGroup({}),
			// string of MonthWeekValue
			rdd: new FormControl(product.rdd),
			// string
			deliveryMill: new FormControl(
				product.deliveryMill || 'eoffer.mill.supplier.choice'
			),
			feasibility: new FormControl(product.feasibilityStatus)
		});
		this.product = product;
	}

	getA2CItemNumber() {
		return <FormControl> this.get('a2citemNumber');
	}

	getInquiryNumber() {
		return <FormControl> this.get('inquiryNumber');
	}

	getFormQuantity() {
		return <FormControl> this.get('quantity');
	}

	getFormThickness() {
		return <FormControl> this.get('thicknessValue');
	}

	getFormWidth() {
		return <FormControl> this.get('widthValue');
	}

	getFormLength() {
		return <FormControl> this.get('lengthValue');
	}

	getFormSteelGrade() {
		return <FormControl> this.get('steelGrade');
	}

	getVariableFormGroup() {
		return <FormGroup> this.get('variableFormGroup');
	}

	getFormShipToParty() {
		return <FormControl> this.get('shipToParty');
	}

	getFormEndUser() {
		return <FormControl> this.get('endUser');
	}

	getSimulationCompleted() {
		return <FormControl> this.get('isSimulationCompleted');
	}

	getConfigErrMsg() {
		return <FormControl> this.get('configErrMsg');
	}

	getLoader() {
		return <FormControl> this.get('loader');
	}

	getConfigurationNumber() {
		return <FormControl> this.get('configurationNumber');
	}

	getProductQuantity() {
		return <number> this.getFormQuantity().value || 0;
	}

	getFormRdd() {
		return <FormControl> this.get('rdd');
	}

	getFormDeliveryMill() {
		return <FormControl> this.get('deliveryMill');
	}

	getFormFeasibility() {
		return <FormControl> this.get('feasibility');
	}

	getUniqueRefValue() {
		const uniqueRefControl = <FormControl> this.get('uniqueRef');
		return <string>uniqueRefControl.value;
	}

	getProduct() {
		this.product.quantity = convertNullToUndefined(
			<number | undefined> this.getFormQuantity().value
		);
		this.product.rdd = convertNullToUndefined(
			<string | undefined> this.getFormRdd().value
		);
		this.product.deliveryMill = convertNullToUndefined(
			<string | undefined> this.getFormDeliveryMill().value
		);
		if (this.product.deliveryMill === 'eoffer.mill.supplier.choice') {
			this.product.deliveryMill = undefined;
		}
		const feasibilityStatus = convertNullToUndefined(
			<FeasibilityStatus> this.getFormFeasibility().value
		);
		this.product.feasibilityStatus = feasibilityStatus || 'PENDING';
		return this.product;
	}

	addProduct(product: OfferProduct) {
		if (product.quantity !== undefined) {
			this.product.quantity = this.product.quantity === undefined
				? product.quantity
				: this.product.quantity + product.quantity;
			this.getFormQuantity().setValue(this.product.quantity);
		}
		if (this.product.rdd === undefined && product.rdd !== undefined) {
			this.product.rdd = product.rdd;
			this.getFormRdd().setValue(this.product.rdd);
		}
		if (this.product.deliveryMill === undefined && product.deliveryMill !== undefined) {
			this.product.deliveryMill = product.deliveryMill;
			this.getFormDeliveryMill().setValue(this.product.deliveryMill);
		}
	}
}

export class FormProducts extends FormArray {
	/**
	 * parent could decomposed into children, child keeps ref to its parent
	 */
	private readonly subscription: Subscription | undefined;
	private readonly parentProducts: FormProducts | undefined;
	constructor(parentProducts?: FormProducts) {
		super([], Validators.minLength(1));
		this.parentProducts = parentProducts;
		if (!this.parentProducts) {
			return;
		}
		this.subscription = this.valueChanges.subscribe(() => {
			if (this.parentProducts) {
				this.parentProducts.updateValueAndValidity();
			}
		});
	}

	isValid() {
		if (this.length === 0) {
			return false;
		}
		for (let i = 0; i < this.length; i++) {
			if (this.getFormProductAt(i).invalid) {
				return false;
			}
		}
		return true;
	}

	getFormProductAt(index: number) {
		return <FormProduct> this.at(index);
	}

	getFormProduct(uniqueRef: string) {
		for (let i = 0; i < this.length; i++) {
			if (this.getFormProductAt(i).product.uniqueRef === uniqueRef) {
				return this.getFormProductAt(i);
			}
		}
		return undefined;
	}

	getFormProductIndex(uniqueRef: string) {
		for (let i = 0; i < this.length; i++) {
			if (this.getFormProductAt(i).product.uniqueRef === uniqueRef) {
				return i;
			}
		}
		return undefined;
	}

	removeProductByUniqueRef(uniqueRef: string) {
		for (let i = 0; i < this.length; i++) {
			if (this.getFormProductAt(i).product.uniqueRef === uniqueRef) {
				this.removeAt(i);
				if (this.parentProducts) {
					this.parentProducts.removeProductByUniqueRef(uniqueRef);
				}
			}
		}
	}

	getFormProducts() {
		return this.controls.map(control => <FormProduct> control);
	}

	addProduct(product: OfferProduct) {
		this.push(new FormProduct(product));
	}

	addProducts(products: OfferProduct[]) {
		for (const product of products) {
			this.push(new FormProduct(product));
		}
	}

	getProductCount() {
		return this.length;
	}

	override clear() {
		while (this.length > 0) {
			this.removeAt(0);
		}
	}

	getProducts() {
		const products: OfferProduct[] = [];
		for (let i = 0; i < this.length; i++) {
			products.push(this.getFormProductAt(i).getProduct());
		}
		return products.length === 0 ? undefined : products;
	}

	setProducts(products: OfferProduct[]) {
		for (const product of products) {
			this.addProduct(product);
			if (this.parentProducts) {
				this.parentProducts.addProduct(product);
			}
		}
	}

	removeAllProducts() {
		this.clear();
	}

	onDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}
}

export class FormPeriod extends FormGroup {
	private periodStartMonth: string | undefined;
	private periodEndMonth: string | undefined;
	constructor() {
		super({
			periodStartMonth: new FormControl(undefined, Validators.required),
			periodEndMonth: new FormControl(undefined, Validators.required)
		});
	}

	refreshLastPeriod() {
		this.periodStartMonth = <string | undefined> this.getFormPeriodStartMonth().value;
		this.periodEndMonth = <string | undefined> this.getFormPeriodEndMonth().value;
	}

	getAndUpdateLastPeriodStartMonth(newStartMonth: string | undefined) {
		const last = this.periodStartMonth;
		this.periodStartMonth = newStartMonth;
		return last;
	}

	getAndUpdateLastPeriodEndMonth(newEndMonth: string | undefined) {
		const last = this.periodEndMonth;
		this.periodEndMonth = newEndMonth;
		return last;
	}

	getFormPeriodStartMonth() {
		return <FormControl> this.get('periodStartMonth');
	}

	getFormPeriodEndMonth() {
		return <FormControl> this.get('periodEndMonth');
	}

	getPeriodStartMonth() {
		return convertNullToUndefined(<string | undefined> this.getFormPeriodStartMonth().value);
	}

	getPeriodEndMonth() {
		return convertNullToUndefined(<string | undefined> this.getFormPeriodEndMonth().value);
	}

	setPeriod(start: string | undefined, end: string | undefined) {
		this.periodStartMonth = start;
		this.periodEndMonth = end;
		this.getFormPeriodStartMonth().setValue(start);
		this.getFormPeriodEndMonth().setValue(end);
	}
}
export class FormOffer extends FormGroup {
	private lastSoldTo: string | undefined;
	private lastShipTo: string | undefined;
	private lastEndUser: string | undefined;
	private offerId: number | undefined;
	private externalOfferId: string | undefined;
	private rfqNumber: string | undefined;
	private offerType: string | undefined | null;

	constructor() {
		super({
			rfqRef: new FormControl(undefined, [Validators.required, Validators.maxLength(255)]), // string
			soldTo: new FormControl(undefined, Validators.required), // businessId: string
			shipTo: new FormControl(undefined, Validators.required), // businessId: string
			endUser: new FormControl(undefined, Validators.required), // businessId: string
			period: new FormPeriod(),
			idd: new FormControl(undefined, Validators.required), // string (idd.code)
			tmode: new FormControl(undefined, Validators.required), // string (tmode.code)
			incoterm: new FormControl(undefined, Validators.required), // string (incoterm.code)
			pricingUnit: new FormControl(undefined, Validators.required), // string (pricingUnit.key)
			currency: new FormControl(undefined, Validators.required), // string (currency.key)
			comment: new FormControl(),
			favorite: new FormControl(false),
			products: new FormProducts(),
			formFamilyByVolumes: new FormFamilyByVolumes()
		});
	}

	refreshLastBoc() {
		this.lastSoldTo = <string | undefined> this.getFormSoldTo().value;
		this.lastShipTo = <string | undefined> this.getFormShipTo().value;
		this.lastEndUser = <string | undefined> this.getFormEndUser().value;
	}

	getAndUpdateLastBoc(newBoc: BocOpt) {
		const soldTo = this.lastSoldTo;
		this.lastSoldTo = newBoc.soldTo;
		const shipTo = this.lastShipTo;
		this.lastShipTo = newBoc.shipTo;
		const endUser = this.lastEndUser;
		this.lastEndUser = newBoc.endUser;
		return { soldTo, shipTo, endUser };
	}

	getOfferId() {
		return this.offerId;
	}

	setOfferId(offerId: number) {
		this.offerId = offerId;
	}

	getExternalOfferId() {
		return this.externalOfferId;
	}

	setExternalOfferId(externalOfferId: string) {
		this.externalOfferId = externalOfferId;
	}

	getrfqNumber() {
		return this.rfqNumber;
	}

	setrfqNumber(rfqNumber: string) {
		this.rfqNumber = rfqNumber;
	}

	getOfferType() {
		return this.offerType;
	}

	setOfferType(offerType: string) {
		this.offerType = offerType;
	}

	getFormRfqRef() {
		return <FormControl> this.get('rfqRef');
	}

	getFormSoldTo() {
		return <FormControl> this.get('soldTo');
	}

	getFormShipTo() {
		return <FormControl> this.get('shipTo');
	}

	getFormEndUser() {
		return <FormControl> this.get('endUser');
	}

	getFormPeriod() {
		return <FormPeriod> this.get('period');
	}

	getFormIdd() {
		return <FormControl> this.get('idd');
	}

	getFormTmode() {
		return <FormControl> this.get('tmode');
	}

	getFormIncoterm() {
		return <FormControl> this.get('incoterm');
	}

	getFormPricingUnit() {
		return <FormControl> this.get('pricingUnit');
	}

	getFormCurrency() {
		return <FormControl> this.get('currency');
	}

	getFormComment() {
		return <FormControl> this.get('comment');
	}

	getFormFavorite() {
		return <FormControl> this.get('favorite');
	}

	getFormProducts() {
		return <FormProducts> this.get('products');
	}

	getFormFamilyByVolumes() {
		return <FormFamilyByVolumes> this.get('formFamilyByVolumes');
	}

	isOfferHeaderValid() {
		return this.getFormRfqRef().valid
			&& this.getFormSoldTo().valid
			&& this.getFormShipTo().valid
			&& this.getFormEndUser().valid
			&& this.getFormPeriod().valid
			&& this.getFormIdd().valid
			&& this.getFormTmode().valid
			&& this.getFormIncoterm().valid
			&& this.getFormPricingUnit().valid
			&& this.getFormCurrency().valid;
	}

	getOffer() {
		return {
			header: {
				id: this.offerId,
				externalId: this.externalOfferId,
				rfqNumber: this.rfqNumber,
				offerType: this.offerType,
				rfqRef: convertNullToUndefined(<string | undefined> this.getFormRfqRef().value),
				soldTo: convertNullToUndefined(<string | undefined> this.getFormSoldTo().value),
				shipTo: convertNullToUndefined(<string | undefined> this.getFormShipTo().value),
				endUser: convertNullToUndefined(<string | undefined> this.getFormEndUser().value),
				periodStartMonth: this.getFormPeriod().getPeriodStartMonth(),
				periodEndMonth: this.getFormPeriod().getPeriodEndMonth(),
				idd: convertNullToUndefined(<string | undefined> this.getFormIdd().value),
				tmode: convertNullToUndefined(<string | undefined> this.getFormTmode().value),
				incoterm: convertNullToUndefined(<string | undefined> this.getFormIncoterm().value),
				pricingUnit: convertNullToUndefined(<string | undefined> this.getFormPricingUnit().value),
				currency: convertNullToUndefined(<string | undefined> this.getFormCurrency().value),
				comment: convertNullToUndefined(<string | undefined> this.getFormComment().value)
			},
			products: this.getFormProducts().getProducts(),
			listOfOfferFamilyVolume: this.getFormFamilyByVolumes().getFamilyByVolumes()
		};
	}

	setOffer(offer: Offer) {
		const { header } = offer;
		this.offerId = header.id;
		this.externalOfferId = header.externalId;
		this.rfqNumber = header.referenceAm;
		this.offerType = header.offerType;
		this.getFormRfqRef().setValue(header.rfqRef);
		this.lastSoldTo = header.soldTo;
		this.getFormSoldTo().setValue(header.soldTo);
		this.lastShipTo = header.shipTo;
		this.getFormShipTo().setValue(header.shipTo);
		this.lastEndUser = header.endUser;
		this.getFormEndUser().setValue(header.endUser);
		this.getFormPeriod().setPeriod(header.periodStartMonth, header.periodEndMonth);
		this.getFormIdd().setValue(header.idd);
		this.getFormPricingUnit().setValue(header.pricingUnit);
		this.getFormTmode().setValue(header.tmode);
		this.getFormIncoterm().setValue(header.incoterm);
		this.getFormCurrency().setValue(header.currency);
		this.getFormProducts().setProducts(offer.products || []);
		this.getFormFamilyByVolumes().setFamilyByVolumes(offer.listOfOfferFamilyVolume || []);
		this.getFormComment().setValue(header.comment);
		return this;
	}
}
